export type EgressProtocol = 'tcp' | 'udp' | 'icmp';
export type EgressPortFieldMode = 'fixed' | 'editable' | 'all' | 'none';

export interface EgressRuleDefinition {
  key: string;
  label: string;
  protocol: EgressProtocol;
  portMode: EgressPortFieldMode;
  port?: number;
}

const catalog: EgressRuleDefinition[] = [
  {
    key: 'amqp',
    label: 'AMQP',
    protocol: 'tcp',
    portMode: 'fixed',
    port: 5672,
  },
  {
    key: 'amqps',
    label: 'AMQPS',
    protocol: 'tcp',
    portMode: 'fixed',
    port: 5671,
  },
  {
    key: 'dns_tcp',
    label: 'DNS (TCP)',
    protocol: 'tcp',
    portMode: 'fixed',
    port: 53,
  },
  {
    key: 'dns_udp',
    label: 'DNS (UDP)',
    protocol: 'udp',
    portMode: 'fixed',
    port: 53,
  },
  {
    key: 'elasticsearch',
    label: 'Elasticsearch',
    protocol: 'tcp',
    portMode: 'fixed',
    port: 9200,
  },
  { key: 'ftp', label: 'FTP', protocol: 'tcp', portMode: 'fixed', port: 21 },
  { key: 'ftps', label: 'FTPS', protocol: 'tcp', portMode: 'fixed', port: 990 },
  { key: 'git', label: 'Git', protocol: 'tcp', portMode: 'fixed', port: 9418 },
  { key: 'http', label: 'HTTP', protocol: 'tcp', portMode: 'fixed', port: 80 },
  {
    key: 'http_alt',
    label: 'HTTP Alternate',
    protocol: 'tcp',
    portMode: 'fixed',
    port: 8080,
  },
  {
    key: 'https',
    label: 'HTTPS',
    protocol: 'tcp',
    portMode: 'fixed',
    port: 443,
  },
  { key: 'icmp', label: 'ICMP', protocol: 'icmp', portMode: 'none' },
  { key: 'imap', label: 'IMAP', protocol: 'tcp', portMode: 'fixed', port: 143 },
  {
    key: 'imaps',
    label: 'IMAPS',
    protocol: 'tcp',
    portMode: 'fixed',
    port: 993,
  },
  {
    key: 'kafka',
    label: 'Kafka',
    protocol: 'tcp',
    portMode: 'fixed',
    port: 9092,
  },
  { key: 'ldap', label: 'LDAP', protocol: 'tcp', portMode: 'fixed', port: 389 },
  {
    key: 'ldaps',
    label: 'LDAPS',
    protocol: 'tcp',
    portMode: 'fixed',
    port: 636,
  },
  {
    key: 'memcached',
    label: 'Memcached',
    protocol: 'tcp',
    portMode: 'fixed',
    port: 11211,
  },
  {
    key: 'mssql',
    label: 'Microsoft SQL Server',
    protocol: 'tcp',
    portMode: 'fixed',
    port: 1433,
  },
  {
    key: 'mongodb',
    label: 'MongoDB',
    protocol: 'tcp',
    portMode: 'fixed',
    port: 27017,
  },
  {
    key: 'mysql',
    label: 'MySQL',
    protocol: 'tcp',
    portMode: 'fixed',
    port: 3306,
  },
  { key: 'ntp', label: 'NTP', protocol: 'udp', portMode: 'fixed', port: 123 },
  { key: 'pop3', label: 'POP3', protocol: 'tcp', portMode: 'fixed', port: 110 },
  {
    key: 'pop3s',
    label: 'POP3S',
    protocol: 'tcp',
    portMode: 'fixed',
    port: 995,
  },
  {
    key: 'postgresql',
    label: 'PostgreSQL',
    protocol: 'tcp',
    portMode: 'fixed',
    port: 5432,
  },
  { key: 'rdp', label: 'RDP', protocol: 'tcp', portMode: 'fixed', port: 3389 },
  {
    key: 'redis',
    label: 'Redis',
    protocol: 'tcp',
    portMode: 'fixed',
    port: 6379,
  },
  { key: 'smtp', label: 'SMTP', protocol: 'tcp', portMode: 'fixed', port: 25 },
  {
    key: 'smtp_submission',
    label: 'SMTP Submission',
    protocol: 'tcp',
    portMode: 'fixed',
    port: 587,
  },
  {
    key: 'smtps',
    label: 'SMTPS',
    protocol: 'tcp',
    portMode: 'fixed',
    port: 465,
  },
  { key: 'snmp', label: 'SNMP', protocol: 'udp', portMode: 'fixed', port: 161 },
  {
    key: 'snmp_trap',
    label: 'SNMP Trap',
    protocol: 'udp',
    portMode: 'fixed',
    port: 162,
  },
  { key: 'ssh', label: 'SSH', protocol: 'tcp', portMode: 'fixed', port: 22 },
  {
    key: 'syslog_udp',
    label: 'Syslog (UDP)',
    protocol: 'udp',
    portMode: 'fixed',
    port: 514,
  },
  { key: 'tcp', label: 'TCP', protocol: 'tcp', portMode: 'editable' },
  { key: 'tcp_all', label: 'TCP All Ports', protocol: 'tcp', portMode: 'all' },
  {
    key: 'telnet',
    label: 'Telnet',
    protocol: 'tcp',
    portMode: 'fixed',
    port: 23,
  },
  { key: 'tftp', label: 'TFTP', protocol: 'udp', portMode: 'fixed', port: 69 },
  { key: 'udp', label: 'UDP', protocol: 'udp', portMode: 'editable' },
  { key: 'udp_all', label: 'UDP All Ports', protocol: 'udp', portMode: 'all' },
];

export const EGRESS_RULE_CATALOG: readonly EgressRuleDefinition[] =
  Object.freeze(
    [...catalog].sort((a, b) => a.label.localeCompare(b.label, 'en')),
  );

export function rulePortFieldMode(
  ruleType: string,
): EgressPortFieldMode | null {
  return (
    EGRESS_RULE_CATALOG.find((entry) => entry.key === ruleType)?.portMode ??
    null
  );
}

export function unsupportedProtocolMessage(
  protocol: EgressProtocol,
): string | null {
  return protocol === 'tcp'
    ? null
    : `${protocol.toUpperCase()} not supported at this time`;
}

export type ParsePortBoundsResult =
  | { ok: true; value: { portStart: number; portEnd: number } }
  | { ok: false; error: string };

export function parsePortBounds(raw: string): ParsePortBoundsResult {
  const value = String(raw ?? '').trim();
  if (!value) return { ok: false, error: 'Enter a port or port range.' };
  const match = /^(\d+)\s*(?:-\s*(\d+))?$/.exec(value);
  if (!match) {
    return {
      ok: false,
      error: 'Use one whole-number port or a dash-separated range.',
    };
  }
  const portStart = Number(match[1]);
  const portEnd = Number(match[2] ?? match[1]);
  if (portStart < 1 || portStart > 65535 || portEnd < 1 || portEnd > 65535) {
    return { ok: false, error: 'Ports must be between 1 and 65535.' };
  }
  if (portStart > portEnd) {
    return {
      ok: false,
      error: 'The first port must not exceed the last port.',
    };
  }
  return { ok: true, value: { portStart, portEnd } };
}

export function formatPortBounds(
  portStart: number | null,
  portEnd: number | null,
  ruleType?: string,
): string {
  if (portStart == null || portEnd == null) {
    return ruleType === 'tcp_all' || ruleType === 'udp_all' ? 'All ports' : '—';
  }
  return portStart === portEnd ? String(portStart) : `${portStart}–${portEnd}`;
}
